import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Alert,
  Button,
  Box,
  CircularProgress,
  Fade,
  Stack,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  SaveOutlined as SaveOutlinedIcon,
  CloudDone as CloudDoneIcon,
  CloudDownloadOutlined as CloudDownloadOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { AuthContext } from "../../../../contexts/AuthContext";
import { useErrorDialog } from "../../../../contexts/ErrorDialogContext";
import BaseDialog from "../../../utils/dialog/BaseDialog";
import ConfirmationDialog from "../../../Console-Panel/RecycleBin/ConfirmationDialog";
import FormCSFilingInfoSection from "./FormCSFilingInfoSection";
import FormCSDataFormSection from "./FormCSDataFormSection";
import FormCSDeclarationSection from "./FormCSDeclarationSection";
import { getFormCSFieldElementId } from "./formCSFieldComponents";
import {
  STEPS,
  createInitialFilingInfo,
  createInitialDeclaration,
  createInitialDeclarationAck,
  createInitialDataFormCS,
  isDeclarationStepValid,
  buildFormCSPayload,
  buildFormCSPrefillPayload,
  applyPrefillPayload,
  formatIrasApiError,
} from "./formCSDefaults";
import {
  formatFormCSValidationErrors,
  validateFormCSDataForm,
} from "./formCSValidation";
import {
  getTaxPrefill,
  saveTaxDraft,
} from "../../../../contexts/services/iras/irasApiServices";
import {
  parseComputationDocBlob,
  buildReferenceSummary,
  pickDefaultTaxComputation,
  parseMainCompDocForPdf,
} from "./formCSReferenceUtils";
import { IRAS_CONFIG } from "../../../../config/irasConfig";

const ASSISTANT_SLOW_LOAD_MS = 8000;
const DRAFT_AUTOSAVE_INTERVAL_MS = 30_000;
const ENABLE_PRE_SUBMIT_VALIDATION = Boolean(
  IRAS_CONFIG?.FEATURE_FLAGS?.ENABLE_FORM_CS_PRE_SUBMIT_VALIDATION
);
const JSON_IMPORT_TEMPLATE = `{
  "filingInfo": {},
  "declaration": {},
  "dataFormCS": {}
}`;

const parseBooleanLike = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return value;
  const normalized = value.trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(normalized)) return true;
  if (["false", "0", "no", "n"].includes(normalized)) return false;
  return value;
};

const buildNormalizedJsonPayload = (
  raw,
  { enforceRequiredSections = true } = {}
) => {
  if (!raw) {
    throw new Error("Please paste a JSON payload before validating.");
  }
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Invalid payload. Please provide a JSON object.");
  }
  if (
    enforceRequiredSections &&
    (!parsed.filingInfo ||
      !parsed.declaration ||
      (!parsed.dataFormCS && !parsed.data))
  ) {
    throw new Error(
      "JSON must include filingInfo, declaration, and dataFormCS sections."
    );
  }
  return {
    ...parsed,
    declaration:
      parsed.declaration && typeof parsed.declaration === "object"
        ? Object.fromEntries(
            Object.entries(parsed.declaration).map(([key, value]) => [
              key,
              parseBooleanLike(value),
            ])
          )
        : parsed.declaration,
  };
};

const isInvalidEisVal = (value) => {
  if (value == null) return true;
  const normalized = String(value).trim().toUpperCase();
  return normalized === "" || normalized === "NA" || normalized === "N/A";
};

const normalizeEisClaimDefaultsForSave = (dataFormCS = {}) => ({
  ...dataFormCS,
  eis_ClaimCashPayout: isInvalidEisVal(dataFormCS.eis_ClaimCashPayout)
    ? "2"
    : dataFormCS.eis_ClaimCashPayout,
  eis_ClaimDedAll: isInvalidEisVal(dataFormCS.eis_ClaimDedAll)
    ? "2"
    : dataFormCS.eis_ClaimDedAll,
});

const FormCSSubmissionDialog = ({
  open,
  onClose,
  onDraftSaved,
  company,
  getTaxComputationList,
  initialDraft = null,
  isIrasAuthenticated = false,
}) => {
  const { global_authState, getMainCompDocOnly, getDetailsPLData } =
    useContext(AuthContext);
  const handleError = useErrorDialog();

  const [activeStep, setActiveStep] = useState(0);
  const [draftSaveStatus, setDraftSaveStatus] = useState("idle");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [taxComputationList, setTaxComputationList] = useState([]);
  const [selectedTcId, setSelectedTcId] = useState(null);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantError, setAssistantError] = useState(null);
  const [mainCompDocBundle, setMainCompDocBundle] = useState(null);
  const [plSnapshot, setPlSnapshot] = useState(null);
  const [assistantLoadSlowHint, setAssistantLoadSlowHint] = useState(false);

  const [filingInfo, setFilingInfo] = useState(() =>
    createInitialFilingInfo(company, global_authState)
  );
  const [declaration, setDeclaration] = useState(createInitialDeclaration);
  const [declarationAck, setDeclarationAck] = useState(
    createInitialDeclarationAck
  );
  const [dataFormCS, setDataFormCS] = useState(createInitialDataFormCS);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [retrieveConfirmOpen, setRetrieveConfirmOpen] = useState(false);
  const [saveCloseHintOpen, setSaveCloseHintOpen] = useState(false);
  const [isRetrievingIrasData, setIsRetrievingIrasData] = useState(false);
  const [jsonFillOpen, setJsonFillOpen] = useState(false);
  const [jsonFillInput, setJsonFillInput] = useState("");
  const [jsonFillError, setJsonFillError] = useState("");
  const [jsonFillValidationError, setJsonFillValidationError] = useState("");
  const [jsonFillValidatedPayload, setJsonFillValidatedPayload] =
    useState(null);
  const [baselineSnapshot, setBaselineSnapshot] = useState(null);
  const [showDataFormErrors, setShowDataFormErrors] = useState(false);
  const contentTopRef = useRef(null);
  const draftSaveInFlightRef = useRef(false);
  const draftSavedFadeRef = useRef(null);
  const retrievedDraftSavedRef = useRef(false);
  const pendingDataFormErrorFieldRef = useRef(null);
  const formSnapshotRef = useRef({
    filingInfo,
    dataFormCS,
    declaration,
    declarationAck,
  });
  formSnapshotRef.current = {
    filingInfo,
    dataFormCS,
    declaration,
    declarationAck,
  };

  const buildFormSnapshot = useCallback(
    () =>
      JSON.stringify({
        activeStep,
        filingInfo,
        declaration,
        declarationAck,
        dataFormCS,
        selectedTcId,
      }),
    [
      activeStep,
      filingInfo,
      declaration,
      declarationAck,
      dataFormCS,
      selectedTcId,
    ]
  );

  const isDirty =
    open &&
    baselineSnapshot != null &&
    buildFormSnapshot() !== baselineSnapshot;

  const scrollContentToTop = useCallback(() => {
    requestAnimationFrame(() => {
      const scrollParent = contentTopRef.current?.closest(
        ".MuiDialogContent-root"
      );
      if (scrollParent) {
        scrollParent.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        contentTopRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
      }
    });
  }, []);

  const scrollToDataFormField = useCallback((fieldKey) => {
    if (!fieldKey) return;

    requestAnimationFrame(() => {
      const fieldElement = document.getElementById(
        getFormCSFieldElementId(fieldKey)
      );
      if (!fieldElement) return;

      const scrollParent = fieldElement.closest(".MuiDialogContent-root");
      if (scrollParent) {
        const parentRect = scrollParent.getBoundingClientRect();
        const fieldRect = fieldElement.getBoundingClientRect();
        scrollParent.scrollTo({
          top: scrollParent.scrollTop + fieldRect.top - parentRect.top - 24,
          behavior: "smooth",
        });
      } else {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      const focusTarget = fieldElement.querySelector(
        "input, textarea, [role='combobox'], button:not([disabled])"
      );
      focusTarget?.focus?.({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    scrollContentToTop();
  }, [activeStep, open, scrollContentToTop]);

  useEffect(() => {
    if (!open) {
      setDraftSaveStatus("idle");
      if (draftSavedFadeRef.current) {
        clearTimeout(draftSavedFadeRef.current);
        draftSavedFadeRef.current = null;
      }
    }
  }, [open]);

  const canAutosaveDraft = useCallback(() => {
    if (!open) return false;
    const uen = filingInfo.uen?.trim();
    const ya = filingInfo.ya;
    return Boolean(uen && ya != null && ya !== "" && !Number.isNaN(Number(ya)));
  }, [open, filingInfo.uen, filingInfo.ya]);

  const persistDraft = useCallback(async () => {
    if (!canAutosaveDraft()) return;
    const payload = buildFormCSPayload({
      filingInfo,
      declaration,
      dataFormCS: normalizeEisClaimDefaultsForSave(dataFormCS),
      declarationAck,
    });
    await saveTaxDraft(payload);
  }, [canAutosaveDraft, filingInfo, declaration, dataFormCS, declarationAck]);

  const persistDraftWithStatus = useCallback(async () => {
    if (!canAutosaveDraft() || draftSaveInFlightRef.current) return false;

    draftSaveInFlightRef.current = true;
    setDraftSaveStatus("saving");
    if (draftSavedFadeRef.current) {
      clearTimeout(draftSavedFadeRef.current);
      draftSavedFadeRef.current = null;
    }

    try {
      await persistDraft();
      setDraftSaveStatus("saved");
      draftSavedFadeRef.current = setTimeout(() => {
        setDraftSaveStatus("idle");
        draftSavedFadeRef.current = null;
      }, 3000);
      return true;
    } catch (draftError) {
      console.warn("Form C-S draft autosave failed:", draftError);
      setDraftSaveStatus("error");
      draftSavedFadeRef.current = setTimeout(() => {
        setDraftSaveStatus("idle");
        draftSavedFadeRef.current = null;
      }, 5000);
      return false;
    } finally {
      draftSaveInFlightRef.current = false;
    }
  }, [canAutosaveDraft, persistDraft]);

  useEffect(() => {
    if (!open || !canAutosaveDraft()) return;

    const intervalId = setInterval(
      persistDraftWithStatus,
      DRAFT_AUTOSAVE_INTERVAL_MS
    );
    return () => clearInterval(intervalId);
  }, [open, canAutosaveDraft, persistDraftWithStatus]);

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setAssistantLoadSlowHint(false);
      return;
    }
    setBaselineSnapshot(null);
    setShowDataFormErrors(false);
    setDiscardConfirmOpen(false);
    setSaveCloseHintOpen(false);
    const baseFormState = {
      filingInfo: createInitialFilingInfo(company, global_authState),
      declaration: createInitialDeclaration(),
      declarationAck: createInitialDeclarationAck(),
      dataFormCS: createInitialDataFormCS(),
    };
    const appliedFormState = initialDraft
      ? applyPrefillPayload(initialDraft, baseFormState)
      : baseFormState;
    const draftYa = appliedFormState.filingInfo?.ya;

    setFilingInfo(appliedFormState.filingInfo);
    setDeclaration(appliedFormState.declaration);
    setDeclarationAck(appliedFormState.declarationAck);
    setDataFormCS(appliedFormState.dataFormCS);

    if (!getTaxComputationList || !company?.c_id) {
      setTaxComputationList([]);
      setSelectedTcId(null);
      return;
    }

    let cancelled = false;
    setListLoading(true);
    setTaxComputationList([]);
    setSelectedTcId(null);
    (async () => {
      try {
        const raw = await getTaxComputationList(company.c_id);
        const arr = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        setTaxComputationList(arr);
        const def =
          draftYa != null && draftYa !== ""
            ? arr.find(
                (item) => Number(item.yearOfAssessment) === Number(draftYa)
              ) || pickDefaultTaxComputation(arr)
            : pickDefaultTaxComputation(arr);
        setSelectedTcId(def?.tc_id ?? null);
        if (
          (draftYa == null || draftYa === "") &&
          def?.yearOfAssessment != null
        ) {
          setFilingInfo((prev) => ({
            ...prev,
            ya: Number(def.yearOfAssessment),
          }));
        }
      } catch (e) {
        if (!cancelled) {
          setTaxComputationList([]);
          setSelectedTcId(null);
          handleError(
            e?.message || "Could not load tax computations for this company."
          );
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    company,
    getTaxComputationList,
    global_authState,
    handleError,
    initialDraft,
  ]);

  useEffect(() => {
    if (!open || listLoading) return;
    setBaselineSnapshot((prev) => (prev === null ? buildFormSnapshot() : prev));
  }, [open, listLoading, buildFormSnapshot]);

  useEffect(() => {
    if (!assistantLoading) {
      setAssistantLoadSlowHint(false);
      return;
    }
    setAssistantLoadSlowHint(false);
    const id = setTimeout(() => {
      setAssistantLoadSlowHint(true);
    }, ASSISTANT_SLOW_LOAD_MS);
    return () => clearTimeout(id);
  }, [assistantLoading]);

  useEffect(() => {
    if (!open) return;

    if (!selectedTcId) {
      setAssistantLoading(false);
      setAssistantError(null);
      setMainCompDocBundle(null);
      setPlSnapshot(null);
      return;
    }

    let cancelled = false;
    setAssistantLoading(true);
    setAssistantError(null);
    setMainCompDocBundle(null);
    setPlSnapshot(null);

    (async () => {
      try {
        const [docResult, plResult] = await Promise.allSettled([
          getMainCompDocOnly(selectedTcId),
          getDetailsPLData(selectedTcId),
        ]);
        if (cancelled) return;

        if (plResult.status === "fulfilled" && plResult.value) {
          setPlSnapshot(plResult.value);
        } else {
          setPlSnapshot(null);
        }

        if (docResult.status === "fulfilled" && docResult.value) {
          const payload = docResult.value;
          if (payload && (payload.doc != null || payload.mainCompSavedAt)) {
            setMainCompDocBundle({
              doc: payload.doc ?? null,
              mainCompSavedAt: payload.mainCompSavedAt ?? null,
            });
            setAssistantError(null);
          } else {
            setMainCompDocBundle(null);
            setAssistantError(
              "No saved Main Tax Computation snapshot was returned."
            );
          }
        } else {
          setMainCompDocBundle(null);
          const err =
            docResult.status === "rejected"
              ? docResult.reason?.message ||
                docResult.reason ||
                "Could not load saved Main Tax Computation snapshot."
              : "No saved Main Tax Computation snapshot was returned.";
          setAssistantError(String(err));
        }
      } catch (e) {
        if (!cancelled) {
          setMainCompDocBundle(null);
          setPlSnapshot(null);
          setAssistantError(
            e?.message ||
              String(e) ||
              "Could not load tax computation data for reference."
          );
        }
      } finally {
        if (!cancelled) setAssistantLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedTcId, getMainCompDocOnly, getDetailsPLData]);

  const selectedTc = useMemo(
    () => taxComputationList.find((t) => t.tc_id === selectedTcId) || null,
    [taxComputationList, selectedTcId]
  );

  const parsedComputationDoc = useMemo(
    () => parseComputationDocBlob(mainCompDocBundle?.doc),
    [mainCompDocBundle?.doc]
  );

  const referenceSummary = useMemo(
    () => buildReferenceSummary(parsedComputationDoc, plSnapshot, selectedTc),
    [parsedComputationDoc, plSnapshot, selectedTc]
  );

  const dataFormValidation = useMemo(
    () => validateFormCSDataForm(dataFormCS),
    [dataFormCS]
  );

  const shouldRunFieldValidation = ENABLE_PRE_SUBMIT_VALIDATION;

  const visibleDataFormFieldErrors =
    shouldRunFieldValidation && showDataFormErrors
      ? dataFormValidation.fieldErrors
      : {};

  useEffect(() => {
    if (!shouldRunFieldValidation) {
      if (showDataFormErrors) setShowDataFormErrors(false);
      return;
    }
    if (showDataFormErrors && dataFormValidation.isValid) {
      setShowDataFormErrors(false);
    }
  }, [
    dataFormValidation.isValid,
    shouldRunFieldValidation,
    showDataFormErrors,
  ]);

  useEffect(() => {
    if (!shouldRunFieldValidation) return;
    if (!showDataFormErrors || activeStep !== 1) return;

    const fieldKey = pendingDataFormErrorFieldRef.current;
    if (!fieldKey) return;

    pendingDataFormErrorFieldRef.current = null;
    scrollToDataFormField(fieldKey);
  }, [
    activeStep,
    shouldRunFieldValidation,
    showDataFormErrors,
    scrollToDataFormField,
  ]);

  const handleTcChange = useCallback(
    (tcId) => {
      setSelectedTcId(tcId);
      const tc = taxComputationList.find((t) => t.tc_id === tcId);
      if (tc?.yearOfAssessment != null) {
        setFilingInfo((prev) => ({
          ...prev,
          ya: Number(tc.yearOfAssessment),
        }));
      }
    },
    [taxComputationList]
  );

  const handleDownloadMainCompPdf = useCallback(async () => {
    const mainCompDoc = mainCompDocBundle?.doc;
    try {
      setPdfGenerating(true);
      const { doc, error } = await parseMainCompDocForPdf(
        mainCompDoc,
        selectedTc
      );
      if (error || !doc) {
        handleError(error || "Could not generate PDF.");
        return;
      }

      const ya =
        selectedTc?.yearOfAssessment != null
          ? `_YA${selectedTc.yearOfAssessment}`
          : "";
      doc.save(`Main_Tax_Computation${ya}_last_saved.pdf`);
    } catch (e) {
      handleError(e?.message || "PDF download failed.");
    } finally {
      setPdfGenerating(false);
    }
  }, [mainCompDocBundle?.doc, selectedTc, handleError]);

  const isFilingStepValid = () =>
    !shouldRunFieldValidation ||
    Boolean(
      filingInfo.uen &&
      filingInfo.ya !== "" &&
      filingInfo.ya != null &&
      !Number.isNaN(Number(filingInfo.ya)) &&
      filingInfo.designationOfFiler &&
      filingInfo.nameOfContactPerson &&
      filingInfo.contactNumber &&
      filingInfo.designationOfContactPerson
    );

  const isDataFormStepValid = () => {
    if (listLoading) return false;
    const hasTaxComputationSelection =
      taxComputationList.length === 0 || Boolean(selectedTcId);
    if (!shouldRunFieldValidation) return hasTaxComputationSelection;
    return hasTaxComputationSelection && dataFormValidation.isValid;
  };

  const isDeclarationValid = () =>
    !shouldRunFieldValidation ||
    isDeclarationStepValid(declaration, declarationAck);

  const handleSaveAndCloseRequest = () => {
    if (busy) return;
    if (shouldRunFieldValidation && !dataFormValidation.isValid) {
      const firstErrorField = dataFormValidation.errors[0]?.field;
      pendingDataFormErrorFieldRef.current = firstErrorField;
      setShowDataFormErrors(true);
      setActiveStep(1);
      handleError(formatFormCSValidationErrors(dataFormValidation.errors));
      scrollToDataFormField(firstErrorField);
      return;
    }
    if (!isDeclarationValid()) {
      handleError(
        "Please complete all required declarations and signature fields before saving."
      );
      return;
    }
    setSaveCloseHintOpen(true);
  };

  const handleConfirmSaveAndClose = async () => {
    if (busy) return;
    const saved = await persistDraftWithStatus();
    if (saved) {
      setSaveCloseHintOpen(false);
      setBaselineSnapshot(buildFormSnapshot());
      retrievedDraftSavedRef.current = false;
      onDraftSaved?.();
      onClose();
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !isFilingStepValid()) return;
    if (activeStep === 1 && !isDataFormStepValid()) {
      if (!shouldRunFieldValidation) return;
      const firstErrorField = dataFormValidation.errors[0]?.field;
      pendingDataFormErrorFieldRef.current = firstErrorField;
      setShowDataFormErrors(true);
      handleError(formatFormCSValidationErrors(dataFormValidation.errors));
      scrollToDataFormField(firstErrorField);
      return;
    }
    if (activeStep === 2 && !isDeclarationValid()) {
      handleError(
        "Please complete all required declarations and signature fields before continuing."
      );
      return;
    }
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleRetrieveIrasData = useCallback(async () => {
    if (isRetrievingIrasData) return;
    if (!isIrasAuthenticated) {
      handleError("Please connect CorpPass before retrieving data from IRAS.");
      setRetrieveConfirmOpen(false);
      return;
    }

    try {
      setIsRetrievingIrasData(true);
      const currentState = {
        filingInfo,
        declaration,
        declarationAck,
        dataFormCS,
      };
      const response = await getTaxPrefill(
        buildFormCSPrefillPayload(currentState)
      );
      if (response?.status !== "success") {
        throw response || new Error("Could not retrieve data from IRAS.");
      }

      const retrievedData =
        response.data?.merged_payload ??
        response.data?.iras_prefill ??
        response.data;
      const applied = applyPrefillPayload(retrievedData, currentState);
      const appliedWithEisDefaults = {
        ...applied,
        dataFormCS: normalizeEisClaimDefaultsForSave(applied.dataFormCS),
      };
      const payload = buildFormCSPayload(appliedWithEisDefaults);

      await saveTaxDraft(payload);
      setFilingInfo(appliedWithEisDefaults.filingInfo);
      setDeclaration(appliedWithEisDefaults.declaration);
      setDeclarationAck(appliedWithEisDefaults.declarationAck);
      setDataFormCS(appliedWithEisDefaults.dataFormCS);
      setBaselineSnapshot(
        JSON.stringify({
          activeStep,
          filingInfo: appliedWithEisDefaults.filingInfo,
          declaration: appliedWithEisDefaults.declaration,
          declarationAck: appliedWithEisDefaults.declarationAck,
          dataFormCS: appliedWithEisDefaults.dataFormCS,
          selectedTcId,
        })
      );
      setDraftSaveStatus("saved");
      retrievedDraftSavedRef.current = true;
      setRetrieveConfirmOpen(false);
      setShowDataFormErrors(false);
      if (draftSavedFadeRef.current) {
        clearTimeout(draftSavedFadeRef.current);
      }
      draftSavedFadeRef.current = setTimeout(() => {
        setDraftSaveStatus("idle");
        draftSavedFadeRef.current = null;
      }, 3000);
    } catch (error) {
      console.warn("Could not retrieve Form C-S data from IRAS:", error);
      handleError(
        formatIrasApiError(
          error,
          "Could not retrieve data from IRAS. Your current form has not been changed."
        )
      );
    } finally {
      setIsRetrievingIrasData(false);
    }
  }, [
    activeStep,
    dataFormCS,
    declaration,
    declarationAck,
    filingInfo,
    handleError,
    isIrasAuthenticated,
    isRetrievingIrasData,
    selectedTcId,
  ]);

  const handleApplyJsonFill = useCallback(() => {
    try {
      if (ENABLE_PRE_SUBMIT_VALIDATION && !jsonFillValidatedPayload) {
        throw new Error("Please validate your JSON first.");
      }
      const fallbackPayload = !ENABLE_PRE_SUBMIT_VALIDATION
        ? buildNormalizedJsonPayload((jsonFillInput || "").trim(), {
            enforceRequiredSections: false,
          })
        : null;
      const currentState = {
        filingInfo,
        declaration,
        declarationAck,
        dataFormCS,
      };
      const applied = applyPrefillPayload(
        jsonFillValidatedPayload || fallbackPayload,
        currentState
      );

      setFilingInfo(applied.filingInfo);
      setDeclaration(applied.declaration);
      setDeclarationAck(applied.declarationAck);
      setDataFormCS(applied.dataFormCS);
      setShowDataFormErrors(false);
      setJsonFillError("");
      setJsonFillValidationError("");
      setJsonFillValidatedPayload(null);
      setJsonFillOpen(false);
    } catch (error) {
      setJsonFillError(error?.message || "Invalid JSON payload.");
    }
  }, [
    dataFormCS,
    declaration,
    declarationAck,
    filingInfo,
    jsonFillInput,
    jsonFillValidatedPayload,
  ]);

  const handleValidateJsonFill = useCallback(() => {
    try {
      const raw = (jsonFillInput || "").trim();
      const normalizedPayload = buildNormalizedJsonPayload(raw, {
        enforceRequiredSections: true,
      });
      const currentState = {
        filingInfo,
        declaration,
        declarationAck,
        dataFormCS,
      };
      const applied = applyPrefillPayload(normalizedPayload, currentState);
      const declarationKeys = Object.keys(createInitialDeclaration());
      const missingDeclarationKeys = declarationKeys.filter(
        (key) => typeof applied.declaration?.[key] !== "boolean"
      );

      const issues = [];
      if (!applied.filingInfo?.uen?.trim()) issues.push("filingInfo.uen");
      if (
        applied.filingInfo?.ya === "" ||
        applied.filingInfo?.ya == null ||
        Number.isNaN(Number(applied.filingInfo?.ya))
      ) {
        issues.push("filingInfo.ya");
      }
      if (!applied.filingInfo?.designationOfFiler?.trim()) {
        issues.push("filingInfo.designationOfFiler");
      }
      if (!applied.filingInfo?.nameOfContactPerson?.trim()) {
        issues.push("filingInfo.nameOfContactPerson");
      }
      if (!applied.filingInfo?.contactNumber?.trim()) {
        issues.push("filingInfo.contactNumber");
      }
      if (!applied.filingInfo?.designationOfContactPerson?.trim()) {
        issues.push("filingInfo.designationOfContactPerson");
      }
      if (missingDeclarationKeys.length) {
        issues.push(
          ...missingDeclarationKeys.map((key) => `declaration.${key}`)
        );
      }

      const dataValidation = validateFormCSDataForm(applied.dataFormCS);
      if (!dataValidation.isValid) {
        issues.push(
          ...dataValidation.errors
            .slice(0, 5)
            .map((item) => item?.field || "dataFormCS")
        );
      }

      if (issues.length) {
        throw new Error(
          `Missing/invalid required fields: ${Array.from(new Set(issues)).join(", ")}`
        );
      }

      setJsonFillError("");
      setJsonFillValidationError("");
      setJsonFillValidatedPayload(normalizedPayload);
    } catch (error) {
      setJsonFillValidatedPayload(null);
      setJsonFillValidationError(
        error?.message || "JSON validation failed. Please check your payload."
      );
    }
  }, [dataFormCS, declaration, declarationAck, filingInfo, jsonFillInput]);

  const handleFormatJsonFill = useCallback(() => {
    try {
      const raw = (jsonFillInput || "").trim();
      if (!raw) {
        throw new Error("Please paste a JSON payload before formatting.");
      }
      const parsed = JSON.parse(raw);
      setJsonFillInput(JSON.stringify(parsed, null, 2));
      setJsonFillError("");
      setJsonFillValidationError("");
      setJsonFillValidatedPayload(null);
    } catch (error) {
      setJsonFillValidationError(
        error?.message || "Unable to format JSON. Please check the syntax."
      );
      setJsonFillValidatedPayload(null);
    }
  }, [jsonFillInput]);

  const handleOpenJsonFill = useCallback(() => {
    setJsonFillError("");
    setJsonFillValidationError("");
    setJsonFillValidatedPayload(null);
    setJsonFillInput((prev) => prev || JSON_IMPORT_TEMPLATE);
    setJsonFillOpen(true);
  }, []);

  const busy = pdfGenerating || isRetrievingIrasData;

  const handleSaveDraft = useCallback(async () => {
    const saved = await persistDraftWithStatus();
    if (saved) {
      setBaselineSnapshot(buildFormSnapshot());
      retrievedDraftSavedRef.current = false;
      onDraftSaved?.();
    }
  }, [buildFormSnapshot, onDraftSaved, persistDraftWithStatus]);

  const handleRequestClose = useCallback(() => {
    if (busy) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    if (retrievedDraftSavedRef.current) {
      retrievedDraftSavedRef.current = false;
      onDraftSaved?.();
    }
    onClose();
  }, [busy, isDirty, onClose, onDraftSaved]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    if (retrievedDraftSavedRef.current) {
      retrievedDraftSavedRef.current = false;
      onDraftSaved?.();
    }
    onClose();
  }, [onClose, onDraftSaved]);

  const stepContent = () => {
    if (activeStep === 0) {
      return (
        <FormCSFilingInfoSection
          filingInfo={filingInfo}
          onChange={setFilingInfo}
          disabled={busy}
        />
      );
    }
    if (activeStep === 1) {
      return (
        <FormCSDataFormSection
          dataFormCS={dataFormCS}
          onChange={setDataFormCS}
          taxComputationList={taxComputationList}
          selectedTcId={selectedTcId}
          onTcChange={handleTcChange}
          listLoading={listLoading}
          selectedTc={selectedTc}
          referenceSummary={referenceSummary}
          parsedMainComp={parsedComputationDoc}
          plSnapshot={plSnapshot}
          mainCompSavedAt={mainCompDocBundle?.mainCompSavedAt}
          assistantLoading={assistantLoading}
          assistantError={
            assistantLoadSlowHint && assistantLoading
              ? "Loading reference data is taking longer than usual."
              : assistantError
          }
          onDownloadPdf={handleDownloadMainCompPdf}
          pdfGenerating={pdfGenerating}
          disabled={busy}
          fieldErrors={visibleDataFormFieldErrors}
        />
      );
    }
    if (activeStep === 2) {
      return (
        <FormCSDeclarationSection
          company={company}
          declaration={declaration}
          onChange={setDeclaration}
          declarationAck={declarationAck}
          onAckChange={setDeclarationAck}
          filingInfo={filingInfo}
          dataFormCS={dataFormCS}
          selectedTc={selectedTc}
          referenceSummary={referenceSummary}
          disabled={busy}
        />
      );
    }
    return null;
  };

  const canGoNext =
    activeStep === 0
      ? isFilingStepValid()
      : activeStep === 1
        ? !listLoading &&
          (taxComputationList.length === 0 || Boolean(selectedTcId))
        : activeStep === 2
          ? isDeclarationValid()
          : false;

  const dialogHeaderStatus = (
    <Stack direction='row' spacing={1.5} alignItems='center'>
      <Button
        variant='outlined'
        size='small'
        onClick={handleOpenJsonFill}
        disabled={busy}
        sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
      >
        Fill from JSON
      </Button>
      <Button
        variant='outlined'
        size='small'
        onClick={() => setRetrieveConfirmOpen(true)}
        disabled={!isIrasAuthenticated || busy}
        startIcon={
          isRetrievingIrasData ? (
            <CircularProgress size={15} />
          ) : (
            <CloudDownloadOutlinedIcon />
          )
        }
        sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
      >
        {isRetrievingIrasData
          ? "Retrieving IRAS Data..."
          : "Retrieve Data from IRAS"}
      </Button>
      <Fade in={draftSaveStatus !== "idle"} timeout={300} unmountOnExit>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.25,
            py: 0.5,
            borderRadius: 1,
            bgcolor: (theme) =>
              draftSaveStatus === "error"
                ? alpha(theme.palette.error.main, 0.08)
                : theme.palette.action.hover,
            color:
              draftSaveStatus === "saved"
                ? "success.main"
                : draftSaveStatus === "error"
                  ? "error.main"
                  : "text.secondary",
          }}
        >
          {draftSaveStatus === "saving" && (
            <>
              <CircularProgress size={14} thickness={5} />
              <Typography
                variant='caption'
                sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
              >
                Saving draft?
              </Typography>
            </>
          )}
          {draftSaveStatus === "saved" && (
            <>
              <CloudDoneIcon sx={{ fontSize: 16 }} />
              <Typography
                variant='caption'
                sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
              >
                Draft saved
              </Typography>
            </>
          )}
          {draftSaveStatus === "error" && (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 16 }} />
              <Typography
                variant='caption'
                sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
              >
                Draft save failed
              </Typography>
            </>
          )}
        </Box>
      </Fade>
    </Stack>
  );

  const dialogActions = (
    <>
      <Button onClick={handleRequestClose} disabled={busy} color='inherit'>
        Cancel
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button
        onClick={handleSaveDraft}
        disabled={busy || draftSaveStatus === "saving" || !canAutosaveDraft()}
        startIcon={
          draftSaveStatus === "saving" ? (
            <CircularProgress size={16} />
          ) : (
            <SaveOutlinedIcon />
          )
        }
      >
        Save draft
      </Button>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: activeStep > 0 ? 1 : 0,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={busy}>
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={
              busy || !canGoNext || (activeStep === 1 && assistantLoading)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            variant='contained'
            onClick={handleSaveAndCloseRequest}
            disabled={busy || draftSaveStatus === "saving"}
            startIcon={
              draftSaveStatus === "saving" ? (
                <CircularProgress size={20} />
              ) : (
                <SaveOutlinedIcon />
              )
            }
            sx={{
              minWidth: 140,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {draftSaveStatus === "saving" ? "Saving..." : "Save & Close"}
          </Button>
        )}
      </Box>
    </>
  );

  return (
    <>
      <BaseDialog
        open={open}
        onClose={() => {}}
        closeDisabled
        maxWidth='xl'
        title='Draft Form C-S (Corporate Tax Return)'
        subtitle='Corporate income tax filing assistant'
        icon={<DescriptionIcon />}
        headerAdornment={dialogHeaderStatus}
        actions={dialogActions}
      >
        <Box ref={contentTopRef} />
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {stepContent()}
      </BaseDialog>

      <ConfirmationDialog
        open={retrieveConfirmOpen}
        onClose={() => {
          if (!isRetrievingIrasData) setRetrieveConfirmOpen(false);
        }}
        onConfirm={handleRetrieveIrasData}
        title='Retrieve data from IRAS?'
        message='This retrieves available IRAS data and updates matching fields in your current Form C-S draft. Your other entries remain available for review.'
        confirmText='Retrieve data'
        confirmColor='success'
        loading={isRetrievingIrasData}
      />

      <ConfirmationDialog
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
        title='Discard unsaved changes?'
        message='You have unsaved changes in this Form C-S submission. Are you sure you want to close without submitting?'
        confirmText='Discard and close'
        confirmColor='error'
      />

      <ConfirmationDialog
        open={saveCloseHintOpen}
        onClose={() => {
          if (draftSaveStatus !== "saving") setSaveCloseHintOpen(false);
        }}
        onConfirm={handleConfirmSaveAndClose}
        title='Not submitted to IRAS'
        message={
          <Box>
            <Typography variant='body1' sx={{ mb: 1.5 }}>
              Saving this declaration only saves your{" "}
              <strong>Form C-S as a draft</strong>. It will{" "}
              <strong>not be submitted to IRAS</strong>.
            </Typography>

            <Typography variant='body1'>
              When you are ready to submit,{" "}
              <strong>log in with your Corppass</strong> to connect to IRAS.
              Then select <strong>Review Declaration &amp; Submit</strong> to
              review and complete your submission to IRAS.
            </Typography>
          </Box>
        }
        confirmText='Save draft & close'
        confirmColor='success'
        loading={draftSaveStatus === "saving"}
      />

      <BaseDialog
        open={jsonFillOpen}
        onClose={() => setJsonFillOpen(false)}
        maxWidth='md'
        title='Fill Form C-S from JSON'
        subtitle='Paste a payload with filingInfo, declaration, and dataFormCS to auto-fill the form.'
        actions={
          <>
            <Button onClick={() => setJsonFillOpen(false)} color='inherit'>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant='text' onClick={handleFormatJsonFill}>
              Format JSON
            </Button>
            {ENABLE_PRE_SUBMIT_VALIDATION ? (
              <Button variant='outlined' onClick={handleValidateJsonFill}>
                Validate JSON
              </Button>
            ) : null}
            <Button
              variant='contained'
              onClick={handleApplyJsonFill}
              disabled={
                ENABLE_PRE_SUBMIT_VALIDATION && !jsonFillValidatedPayload
              }
            >
              Fill Form
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
          <Alert severity='info'>
            {ENABLE_PRE_SUBMIT_VALIDATION
              ? 'The JSON parser accepts boolean strings like "True"/"False".'
              : "Validation is disabled by feature flag. JSON will be filled directly after syntax parsing."}
          </Alert>
          <TextField
            multiline
            minRows={14}
            value={jsonFillInput}
            onChange={(event) => {
              setJsonFillInput(event.target.value);
              setJsonFillError("");
              setJsonFillValidationError("");
              setJsonFillValidatedPayload(null);
            }}
            placeholder={JSON_IMPORT_TEMPLATE}
            fullWidth
          />
          {ENABLE_PRE_SUBMIT_VALIDATION && jsonFillValidatedPayload ? (
            <Alert severity='success'>
              JSON is valid. You can now click Fill Form.
            </Alert>
          ) : null}
          {ENABLE_PRE_SUBMIT_VALIDATION && jsonFillValidationError ? (
            <Alert severity='error'>{jsonFillValidationError}</Alert>
          ) : null}
          {jsonFillError ? (
            <Alert severity='error'>{jsonFillError}</Alert>
          ) : null}
        </Stack>
      </BaseDialog>
    </>
  );
};

export default FormCSSubmissionDialog;
